import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface EditorPresence {
  user_id: string;
  user_name: string;
}

/**
 * Call inside Editor.tsx.
 * Tracks the current user on a per-sermon Realtime presence channel while
 * the editor is mounted. Automatically unregisters on unmount.
 *
 * Pass enabled=false to suppress broadcasting (e.g. for viewers).
 */
export function useEditorPresence(
  sermonId: string | undefined,
  enabled: boolean = true
) {
  const { user } = useAuth();

  useEffect(() => {
    if (!sermonId || !user || !enabled) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;

    (async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, first_name, last_name')
        .eq('id', user.id)
        .maybeSingle();

      const userName =
        profile?.full_name ||
        (profile?.first_name && profile?.last_name
          ? `${profile.first_name} ${profile.last_name}`
          : profile?.first_name) ||
        user.email?.split('@')[0] ||
        'Someone';

      channel = supabase.channel(`sermon-editing:${sermonId}`, {
        config: { presence: { key: user.id } },
      });

      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel!.track({ user_id: user.id, user_name: userName });
        }
      });
    })();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, [sermonId, user?.id, enabled]);
}

/**
 * Call inside SermonList.tsx.
 * Subscribes (read-only) to presence channels for all provided sermon IDs
 * and returns a map of sermonId → [users currently editing].
 * The current user is always excluded so you never see yourself.
 */
export function usePresenceForSermons(
  sermonIds: string[]
): Record<string, EditorPresence[]> {
  const { user } = useAuth();
  const [presenceMap, setPresenceMap] = useState<Record<string, EditorPresence[]>>({});
  const channelsRef = useRef(new Map<string, ReturnType<typeof supabase.channel>>());

  // Stable string so the effect only re-runs when the actual set of IDs changes
  const idsKey = [...sermonIds].sort().join(',');

  useEffect(() => {
    if (!user) return;

    const idSet = new Set(sermonIds);

    // Tear down channels for sermons no longer in the visible list
    for (const [id, ch] of channelsRef.current) {
      if (!idSet.has(id)) {
        supabase.removeChannel(ch);
        channelsRef.current.delete(id);
        setPresenceMap(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    }

    // Subscribe to new sermon IDs
    for (const sermonId of sermonIds) {
      if (channelsRef.current.has(sermonId)) continue;

      const channel = supabase.channel(`sermon-editing:${sermonId}`, {
        config: { presence: { key: user.id } },
      });

      channel
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState<EditorPresence>();
          const editors: EditorPresence[] = Object.entries(state)
            .filter(([key]) => key !== user.id)
            .flatMap(([, presences]) => presences as EditorPresence[]);
          setPresenceMap(prev => ({ ...prev, [sermonId]: editors }));
        })
        .subscribe();

      channelsRef.current.set(sermonId, channel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, user?.id]);

  // Full teardown when SermonList unmounts
  useEffect(() => {
    return () => {
      for (const ch of channelsRef.current.values()) {
        supabase.removeChannel(ch);
      }
      channelsRef.current.clear();
    };
  }, []);

  return presenceMap;
}
